// Passos do "Trabalhe Conosco em etapas": dados, código, currículo, etapas do builder,
// revisão/consentimento e sucesso. Sem `window`/`document` no render (o bloco é renderizado
// no SSR junto com a página); o que toca navegador fica em handlers e efeitos.
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import type { TcCampo, TcEtapa, TcRespostas, TcVaga } from '../../lib/tc/types';
import { Icone } from '../avm/AvmUI';
import type { TcCurriculoEnviado, TcDados, TcErros } from './tc.utils';
import {
    aplicarMascara,
    colunasDoCampo,
    formatarBytes,
    mascaraTelefone,
    soDigitos,
    TC_MAX_TEXTO_CURTO,
    TC_MAX_TEXTO_LONGO,
} from './tc.utils';
import styles from './TrabalheConosco.module.css';

/* ── ícones extras (os da AVM não têm upload/documento/e-mail) ──────────────── */

type NomeIconeTc = 'upload' | 'documento' | 'email' | 'lixeira';

const TRACOS_TC: Record<NomeIconeTc, string> = {
    upload: 'M12 16V4M7 9l5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2',
    documento: 'M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8l-5-5ZM14 3v5h5M9 13h6M9 17h6',
    email: 'M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1ZM3.5 7.5 12 13l8.5-5.5',
    lixeira: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
};

export const IconeTc = ({ nome, tamanho = 24, trecho = 1.6 }: { nome: NomeIconeTc; tamanho?: number; trecho?: number }) => (
    <svg
        width={tamanho}
        height={tamanho}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={trecho}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d={TRACOS_TC[nome]} />
    </svg>
);

/* ── campo (rótulo + erro + ajuda) ─────────────────────────────────────────── */

export const TcCampoWrap = ({
    id,
    rotulo,
    opcional,
    erro,
    ajuda,
    grupo,
    children,
}: {
    id: string;
    rotulo: string;
    opcional?: boolean;
    erro?: string;
    ajuda?: string;
    /** chips/cartões: o rótulo vira legenda de um grupo em vez de <label for>. */
    grupo?: boolean;
    children: React.ReactNode;
}) => {
    const rotuloNo = (
        <>
            {rotulo}
            {opcional ? <span className={styles.opcional}>(opcional)</span> : null}
        </>
    );
    const rodape = erro ? (
        <span className={styles.erro} id={`${id}-erro`} role="alert">
            <Icone nome="alerta" tamanho={14} trecho={2} />
            {erro}
        </span>
    ) : ajuda ? (
        <span className={styles.ajuda} id={`${id}-ajuda`}>
            {ajuda}
        </span>
    ) : null;

    if (grupo) {
        return (
            <div
                className={styles.campo}
                role="group"
                aria-labelledby={`${id}-rotulo`}
                aria-describedby={erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined}
                id={`${id}-grupo`}
            >
                <span className={styles.rotulo} id={`${id}-rotulo`}>
                    {rotuloNo}
                </span>
                {children}
                {rodape}
            </div>
        );
    }
    return (
        <div className={styles.campo}>
            <label className={styles.rotulo} htmlFor={id}>
                {rotuloNo}
            </label>
            {children}
            {rodape}
        </div>
    );
};

const ariaDoCampo = (id: string, erro?: string, ajuda?: string) => ({
    'aria-invalid': erro ? (true as const) : undefined,
    'aria-describedby': erro ? `${id}-erro` : ajuda ? `${id}-ajuda` : undefined,
});

/* ── 1. Seus dados ─────────────────────────────────────────────────────────── */

export interface PassoDadosProps {
    dados: TcDados;
    hp: string;
    setHp: (_v: string) => void;
    setDado: <K extends keyof TcDados>(_campo: K, _valor: TcDados[K]) => void;
    aoSair: (_campo: keyof TcDados) => void;
    erros: TcErros;
    travado: boolean;
    idBase: string;
    mostrarVagas: boolean;
    /** null = ainda carregando; [] = sem vaga ativa (os chips somem). */
    vagas: TcVaga[] | null;
}

export const PassoDados = ({ dados, hp, setHp, setDado, aoSair, erros, travado, idBase, mostrarVagas, vagas }: PassoDadosProps) => {
    const alternarVaga = (id: number) => {
        const tem = dados.vagas.indexOf(id) !== -1;
        setDado('vagas', tem ? dados.vagas.filter((v) => v !== id) : dados.vagas.concat(id));
    };
    const mostraChips = mostrarVagas && (vagas === null || vagas.length > 0);

    return (
        <div className={styles.campos}>
            {/* honeypot: fora da tela e fora da navegação por teclado (§3: `__hp`) */}
            <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor={`${idBase}-hp`}>Não preencha este campo</label>
                <input
                    id={`${idBase}-hp`}
                    name="__hp"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                />
            </div>

            <TcCampoWrap id={`${idBase}-nome`} rotulo="Nome completo" erro={erros.nome}>
                <input
                    id={`${idBase}-nome`}
                    name="nome"
                    type="text"
                    className={`${styles.input} ${erros.nome ? styles.inputErro : ''}`}
                    value={dados.nome}
                    autoComplete="name"
                    maxLength={120}
                    disabled={travado}
                    placeholder="Como está no seu documento"
                    {...ariaDoCampo(`${idBase}-nome`, erros.nome)}
                    onChange={(e) => setDado('nome', e.target.value)}
                    onBlur={() => aoSair('nome')}
                />
            </TcCampoWrap>

            <div className={styles.grade}>
                <div className={styles.gradeItem} style={{ ['--tc-col' as any]: 7 }}>
                    <TcCampoWrap id={`${idBase}-email`} rotulo="E-mail" erro={erros.email} ajuda="Vamos enviar um código de confirmação para ele.">
                        <input
                            id={`${idBase}-email`}
                            name="email"
                            type="email"
                            inputMode="email"
                            className={`${styles.input} ${erros.email ? styles.inputErro : ''}`}
                            value={dados.email}
                            autoComplete="email"
                            autoCapitalize="none"
                            maxLength={160}
                            disabled={travado}
                            placeholder="voce@exemplo.com"
                            {...ariaDoCampo(`${idBase}-email`, erros.email, 'ajuda')}
                            onChange={(e) => setDado('email', e.target.value)}
                            onBlur={() => aoSair('email')}
                        />
                    </TcCampoWrap>
                </div>
                <div className={styles.gradeItem} style={{ ['--tc-col' as any]: 5 }}>
                    <TcCampoWrap id={`${idBase}-celular`} rotulo="Celular" erro={erros.celular}>
                        <input
                            id={`${idBase}-celular`}
                            name="celular"
                            type="tel"
                            inputMode="tel"
                            className={`${styles.input} ${erros.celular ? styles.inputErro : ''}`}
                            value={dados.celular}
                            autoComplete="tel-national"
                            maxLength={16}
                            disabled={travado}
                            placeholder="(11) 99999-9999"
                            {...ariaDoCampo(`${idBase}-celular`, erros.celular)}
                            onChange={(e) => setDado('celular', mascaraTelefone(e.target.value))}
                            onBlur={() => aoSair('celular')}
                        />
                    </TcCampoWrap>
                </div>
            </div>

            <TcCampoWrap id={`${idBase}-cidade`} rotulo="Cidade onde mora" opcional erro={erros.cidade}>
                <input
                    id={`${idBase}-cidade`}
                    name="cidade"
                    type="text"
                    className={`${styles.input} ${erros.cidade ? styles.inputErro : ''}`}
                    value={dados.cidade}
                    autoComplete="address-level2"
                    maxLength={120}
                    disabled={travado}
                    placeholder="Cidade / UF"
                    {...ariaDoCampo(`${idBase}-cidade`, erros.cidade)}
                    onChange={(e) => setDado('cidade', e.target.value)}
                />
            </TcCampoWrap>

            {mostraChips ? (
                <TcCampoWrap
                    id={`${idBase}-vagas`}
                    rotulo="Vagas de interesse"
                    opcional
                    grupo
                    erro={erros.vagas}
                    ajuda={vagas === null ? 'Carregando as vagas abertas…' : 'Pode escolher mais de uma.'}
                >
                    <div className={styles.chips}>
                        {vagas === null ? (
                            <>
                                <span className={styles.esqueleto} />
                                <span className={styles.esqueleto} />
                            </>
                        ) : (
                            vagas.map((vaga) => {
                                const ativo = dados.vagas.indexOf(vaga.id) !== -1;
                                return (
                                    <button
                                        key={vaga.id}
                                        type="button"
                                        className={`${styles.chip} ${ativo ? styles.chipAtivo : ''}`}
                                        role="checkbox"
                                        aria-checked={ativo}
                                        title={vaga.descricao || undefined}
                                        disabled={travado}
                                        onClick={() => alternarVaga(vaga.id)}
                                    >
                                        {ativo ? <Icone nome="check" tamanho={14} trecho={2.6} /> : null}
                                        {vaga.titulo}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </TcCampoWrap>
            ) : null}
        </div>
    );
};

/* ── 2. Confirme seu e-mail ────────────────────────────────────────────────── */

export const TAMANHO_CODIGO = 6;

export interface PassoCodigoProps {
    emailMascarado: string | null;
    codigo: string;
    setCodigo: (_v: string) => void;
    /** chamado quando o 6º dígito entra (o pai dispara a verificação). */
    aoCompletar: (_codigo: string) => void;
    erro: string | null;
    aviso: string | null;
    travado: boolean;
    idBase: string;
    /** segundos até poder reenviar (0 = liberado) */
    contador: number;
    reenviando: boolean;
    aoReenviar: () => void;
    aoTrocarEmail: () => void;
}

export const PassoCodigo = ({
    emailMascarado,
    codigo,
    setCodigo,
    aoCompletar,
    erro,
    aviso,
    travado,
    idBase,
    contador,
    reenviando,
    aoReenviar,
    aoTrocarEmail,
}: PassoCodigoProps) => {
    const caixas = useRef<(HTMLInputElement | null)[]>([]);

    const focar = (i: number) => {
        const alvo = caixas.current[Math.max(0, Math.min(TAMANHO_CODIGO - 1, i))];
        if (alvo) {
            alvo.focus();
            try {
                alvo.select();
            } catch {
                /* alguns navegadores não deixam selecionar input numérico */
            }
        }
    };

    const aplicar = (novo: string, posicaoFoco: number) => {
        const limpo = soDigitos(novo).slice(0, TAMANHO_CODIGO);
        setCodigo(limpo);
        focar(posicaoFoco);
        if (limpo.length === TAMANHO_CODIGO) aoCompletar(limpo);
    };

    const aoMudar = (i: number, valor: string) => {
        const digitos = soDigitos(valor);
        if (!digitos) {
            // apagou o conteúdo da caixa
            aplicar(codigo.slice(0, i), i);
            return;
        }
        if (digitos.length > 1) {
            // colou (ou o autocomplete de SMS/e-mail despejou tudo numa caixa só)
            const novo = codigo.slice(0, i) + digitos;
            aplicar(novo, Math.min(TAMANHO_CODIGO - 1, i + digitos.length));
            return;
        }
        const novo = (codigo.slice(0, i) + digitos + codigo.slice(i + 1)).slice(0, TAMANHO_CODIGO);
        aplicar(novo, i + 1);
    };

    const aoTeclar = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !codigo.charAt(i)) {
            e.preventDefault();
            aplicar(codigo.slice(0, Math.max(0, i - 1)), i - 1);
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focar(i - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focar(i + 1);
        }
    };

    const aoColar = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const texto = e.clipboardData ? e.clipboardData.getData('text') : '';
        const digitos = soDigitos(texto);
        if (!digitos) return;
        e.preventDefault();
        aplicar(digitos, Math.min(TAMANHO_CODIGO - 1, digitos.length));
    };

    const indices: number[] = [];
    for (let i = 0; i < TAMANHO_CODIGO; i += 1) indices.push(i);

    return (
        <div className={styles.campos}>
            <p className={styles.emailConfirmacao}>
                <IconeTc nome="email" tamanho={18} /> Enviamos um código para{' '}
                <strong>{emailMascarado || 'o seu e-mail'}</strong>. Confira também a caixa de spam.
            </p>

            {aviso ? (
                <div className={styles.avisoInfo} role="status">
                    <span className={styles.avisoIcone}>
                        <Icone nome="alerta" tamanho={18} trecho={2} />
                    </span>
                    <span>{aviso}</span>
                </div>
            ) : null}

            <TcCampoWrap id={`${idBase}-codigo`} rotulo="Código de 6 dígitos" grupo erro={erro || undefined}>
                <div className={styles.codigo}>
                    {indices.map((i) => (
                        <input
                            key={i}
                            ref={(el) => {
                                caixas.current[i] = el;
                            }}
                            id={i === 0 ? `${idBase}-codigo` : `${idBase}-codigo-${i}`}
                            className={`${styles.codigoCaixa} ${erro ? styles.codigoCaixaErro : ''}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete={i === 0 ? 'one-time-code' : 'off'}
                            aria-label={`Dígito ${i + 1} de ${TAMANHO_CODIGO}`}
                            aria-invalid={erro ? true : undefined}
                            value={codigo.charAt(i)}
                            disabled={travado}
                            onChange={(e) => aoMudar(i, e.target.value)}
                            onKeyDown={(e) => aoTeclar(i, e)}
                            onPaste={aoColar}
                            onFocus={(e) => {
                                try {
                                    e.target.select();
                                } catch {
                                    /* ignora */
                                }
                            }}
                        />
                    ))}
                </div>
            </TcCampoWrap>

            <div className={styles.acoesCodigo}>
                <button type="button" className={styles.botaoLink} disabled={travado || reenviando || contador > 0} onClick={aoReenviar}>
                    {reenviando ? 'Reenviando…' : contador > 0 ? `Reenviar código em ${contador}s` : 'Reenviar código'}
                </button>
                <button type="button" className={styles.botaoLink} disabled={travado} onClick={aoTrocarEmail}>
                    Trocar e-mail
                </button>
            </div>
        </div>
    );
};

/* ── 3. Seu currículo ──────────────────────────────────────────────────────── */

export interface PassoCurriculoProps {
    curriculo: TcCurriculoEnviado | null;
    /** null = sem upload em curso; 0..100 = enviando */
    progresso: number | null;
    nomeEnviando: string | null;
    erro: string | null;
    travado: boolean;
    idBase: string;
    obrigatorio: boolean;
    aoEscolher: (_arquivo: File) => void;
    aoTrocar: () => void;
}

export const PassoCurriculo = ({ curriculo, progresso, nomeEnviando, erro, travado, idBase, obrigatorio, aoEscolher, aoTrocar }: PassoCurriculoProps) => {
    const [arrastando, setArrastando] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const enviando = progresso !== null;

    const abrir = () => {
        if (travado || enviando) return;
        if (inputRef.current) inputRef.current.click();
    };

    const receber = (lista: FileList | null | undefined) => {
        const arquivo = lista && lista.length ? lista[0] : null;
        if (arquivo) aoEscolher(arquivo);
    };

    if (curriculo && !enviando) {
        return (
            <div className={styles.campos}>
                <div className={styles.upload} role="status">
                    <div className={styles.uploadLinha}>
                        <span className={`${styles.uploadIcone} ${styles.uploadIconeOk}`}>
                            <Icone nome="check" tamanho={20} trecho={2.4} />
                        </span>
                        <span className={styles.uploadTexto}>
                            <span className={styles.arquivoNome}>{curriculo.nome}</span>
                            <span className={styles.arquivoMeta}>
                                Enviado ✓{curriculo.bytes ? ` · ${formatarBytes(curriculo.bytes)}` : ''}
                            </span>
                        </span>
                        <button type="button" className={styles.botaoLink} disabled={travado} onClick={aoTrocar}>
                            Trocar arquivo
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.campos}>
            <TcCampoWrap id={`${idBase}-curriculo`} rotulo="Currículo em PDF" opcional={!obrigatorio} grupo erro={erro || undefined}>
                <input
                    ref={inputRef}
                    id={`${idBase}-curriculo-arquivo`}
                    className={styles.arquivoOculto}
                    type="file"
                    accept="application/pdf,.pdf"
                    tabIndex={-1}
                    aria-hidden="true"
                    disabled={travado || enviando}
                    onChange={(e) => {
                        receber(e.target.files);
                        e.target.value = '';
                    }}
                />
                {enviando ? (
                    <div className={styles.upload} aria-live="polite">
                        <div className={styles.uploadLinha}>
                            <span className={styles.uploadIcone}>
                                <IconeTc nome="documento" tamanho={20} />
                            </span>
                            <span className={styles.uploadTexto}>
                                <span className={styles.arquivoNome}>{nomeEnviando || 'Currículo'}</span>
                                <span className={styles.arquivoMeta}>Enviando… {progresso}%</span>
                            </span>
                        </div>
                        <div
                            className={styles.uploadBarra}
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={progresso || 0}
                            aria-label="Envio do currículo"
                        >
                            <div className={styles.uploadBarraPreenchida} style={{ width: `${progresso || 0}%` }} />
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        id={`${idBase}-curriculo`}
                        className={`${styles.dropzone} ${arrastando ? styles.dropzoneAtiva : ''} ${erro ? styles.dropzoneErro : ''}`}
                        disabled={travado}
                        aria-describedby={erro ? `${idBase}-curriculo-erro` : undefined}
                        onClick={abrir}
                        onDragOver={(e) => {
                            e.preventDefault();
                            if (!arrastando) setArrastando(true);
                        }}
                        onDragLeave={() => setArrastando(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setArrastando(false);
                            if (travado) return;
                            receber(e.dataTransfer ? e.dataTransfer.files : null);
                        }}
                    >
                        <span className={styles.dropzoneIcone}>
                            <IconeTc nome="upload" tamanho={26} trecho={1.8} />
                        </span>
                        <span className={styles.dropzoneTexto}>
                            Arraste o PDF aqui ou <u>escolha o arquivo</u>
                        </span>
                        <span className={styles.dropzoneDica}>Somente PDF · até 5 MB</span>
                    </button>
                )}
            </TcCampoWrap>
        </div>
    );
};

/* ── 4..N. Etapas do builder ───────────────────────────────────────────────── */

export interface PassoEtapaProps {
    etapa: TcEtapa;
    respostas: TcRespostas;
    setResposta: (_chave: string, _valor: string | string[]) => void;
    aoSair: (_chave: string) => void;
    erros: TcErros;
    travado: boolean;
    idBase: string;
}

const CampoEtapa = ({
    campo,
    valor,
    erro,
    travado,
    idBase,
    setResposta,
    aoSair,
}: {
    campo: TcCampo;
    valor: string | string[] | undefined;
    erro?: string;
    travado: boolean;
    idBase: string;
    setResposta: (_chave: string, _valor: string | string[]) => void;
    aoSair: (_chave: string) => void;
}) => {
    const id = `${idBase}-c-${campo.key}`;
    const texto = Array.isArray(valor) ? '' : String(valor ?? '');
    const lista = Array.isArray(valor) ? valor : [];

    if (campo.type === 'long') {
        return (
            <TcCampoWrap id={id} rotulo={campo.label} opcional={!campo.required} erro={erro} ajuda={campo.help || undefined}>
                <textarea
                    id={id}
                    name={campo.key}
                    className={`${styles.textarea} ${erro ? styles.inputErro : ''}`}
                    value={texto}
                    maxLength={TC_MAX_TEXTO_LONGO}
                    disabled={travado}
                    rows={5}
                    {...ariaDoCampo(id, erro, campo.help)}
                    onChange={(e) => setResposta(campo.key, e.target.value)}
                    onBlur={() => aoSair(campo.key)}
                />
                <span className={styles.contador} aria-hidden="true">
                    {texto.length}/{TC_MAX_TEXTO_LONGO}
                </span>
            </TcCampoWrap>
        );
    }

    if (campo.type === 'select' || campo.type === 'single') {
        return (
            <TcCampoWrap id={id} rotulo={campo.label} opcional={!campo.required} grupo erro={erro} ajuda={campo.help || undefined}>
                <div className={`${styles.opcoes} ${erro ? styles.opcoesErro : ''}`} role="radiogroup" aria-labelledby={`${id}-rotulo`}>
                    {campo.options.map((opcao, i) => {
                        const ativo = texto === opcao;
                        return (
                            <button
                                key={opcao}
                                type="button"
                                id={i === 0 ? id : undefined}
                                className={`${styles.opcao} ${ativo ? styles.opcaoAtiva : ''}`}
                                role="radio"
                                aria-checked={ativo}
                                disabled={travado}
                                onClick={() => setResposta(campo.key, ativo && !campo.required ? '' : opcao)}
                            >
                                <span className={styles.opcaoMarca}>
                                    <Icone nome="check" tamanho={13} trecho={3} />
                                </span>
                                <span>{opcao}</span>
                            </button>
                        );
                    })}
                </div>
            </TcCampoWrap>
        );
    }

    if (campo.type === 'multi') {
        const alternar = (opcao: string) => {
            const tem = lista.indexOf(opcao) !== -1;
            setResposta(campo.key, tem ? lista.filter((v) => v !== opcao) : lista.concat(opcao));
        };
        return (
            <TcCampoWrap id={id} rotulo={campo.label} opcional={!campo.required} grupo erro={erro} ajuda={campo.help || 'Pode escolher mais de uma.'}>
                <div className={`${styles.chips} ${erro ? styles.chipsErro : ''}`}>
                    {campo.options.map((opcao, i) => {
                        const ativo = lista.indexOf(opcao) !== -1;
                        return (
                            <button
                                key={opcao}
                                type="button"
                                id={i === 0 ? id : undefined}
                                className={`${styles.chip} ${ativo ? styles.chipAtivo : ''}`}
                                role="checkbox"
                                aria-checked={ativo}
                                disabled={travado}
                                onClick={() => alternar(opcao)}
                            >
                                {ativo ? <Icone nome="check" tamanho={14} trecho={2.6} /> : null}
                                {opcao}
                            </button>
                        );
                    })}
                </div>
            </TcCampoWrap>
        );
    }

    // short
    const tipoInput = campo.validate === 'email' ? 'email' : campo.validate === 'telefone' ? 'tel' : 'text';
    const modo =
        campo.validate === 'email'
            ? 'email'
            : campo.validate === 'telefone'
              ? 'tel'
              : campo.validate === 'cpf' || campo.validate === 'cnpj' || campo.validate === 'data'
                ? 'numeric'
                : undefined;
    const placeholder =
        campo.validate === 'data' ? 'dd/mm/aaaa' : campo.validate === 'cpf' ? '000.000.000-00' : campo.validate === 'cnpj' ? '00.000.000/0000-00' : undefined;
    return (
        <TcCampoWrap id={id} rotulo={campo.label} opcional={!campo.required} erro={erro} ajuda={campo.help || undefined}>
            <input
                id={id}
                name={campo.key}
                type={tipoInput}
                inputMode={modo}
                className={`${styles.input} ${erro ? styles.inputErro : ''}`}
                value={texto}
                maxLength={TC_MAX_TEXTO_CURTO}
                disabled={travado}
                placeholder={placeholder}
                autoComplete="off"
                {...ariaDoCampo(id, erro, campo.help)}
                onChange={(e) => setResposta(campo.key, aplicarMascara(campo, e.target.value))}
                onBlur={() => aoSair(campo.key)}
            />
        </TcCampoWrap>
    );
};

export const PassoEtapa = ({ etapa, respostas, setResposta, aoSair, erros, travado, idBase }: PassoEtapaProps) => (
    <div className={styles.grade}>
        {etapa.campos.map((campo) => (
            <div key={campo.key} className={styles.gradeItem} style={{ ['--tc-col' as any]: colunasDoCampo(campo) }}>
                <CampoEtapa
                    campo={campo}
                    valor={respostas[campo.key]}
                    erro={erros[campo.key]}
                    travado={travado}
                    idBase={idBase}
                    setResposta={setResposta}
                    aoSair={aoSair}
                />
            </div>
        ))}
    </div>
);

/* ── Último: revisar e enviar ──────────────────────────────────────────────── */

export interface PassoRevisaoProps {
    dados: TcDados;
    vagas: TcVaga[] | null;
    curriculo: TcCurriculoEnviado | null;
    curriculoAtivo: boolean;
    totalRespostas: number;
    consentimento: boolean;
    setConsentimento: (_v: boolean) => void;
    consentText: string;
    erro?: string;
    travado: boolean;
    idBase: string;
}

export const PassoRevisao = ({
    dados,
    vagas,
    curriculo,
    curriculoAtivo,
    totalRespostas,
    consentimento,
    setConsentimento,
    consentText,
    erro,
    travado,
    idBase,
}: PassoRevisaoProps) => {
    const nomesVagas = (vagas || []).filter((v) => dados.vagas.indexOf(v.id) !== -1).map((v) => v.titulo);
    return (
        <div className={styles.campos}>
            <dl className={styles.resumo}>
                <div className={styles.resumoItem}>
                    <dt>Nome</dt>
                    <dd>{dados.nome}</dd>
                </div>
                <div className={styles.resumoItem}>
                    <dt>E-mail</dt>
                    <dd>{dados.email}</dd>
                </div>
                <div className={styles.resumoItem}>
                    <dt>Celular</dt>
                    <dd>{dados.celular}</dd>
                </div>
                {dados.cidade.trim() ? (
                    <div className={styles.resumoItem}>
                        <dt>Cidade</dt>
                        <dd>{dados.cidade}</dd>
                    </div>
                ) : null}
                {nomesVagas.length ? (
                    <div className={styles.resumoItem}>
                        <dt>Vagas</dt>
                        <dd>{nomesVagas.join(', ')}</dd>
                    </div>
                ) : null}
                {curriculoAtivo ? (
                    <div className={styles.resumoItem}>
                        <dt>Currículo</dt>
                        <dd>{curriculo ? curriculo.nome : 'Não enviado'}</dd>
                    </div>
                ) : null}
                {totalRespostas > 0 ? (
                    <div className={styles.resumoItem}>
                        <dt>Respostas</dt>
                        <dd>
                            {totalRespostas} {totalRespostas === 1 ? 'pergunta respondida' : 'perguntas respondidas'}
                        </dd>
                    </div>
                ) : null}
            </dl>

            <label className={`${styles.consent} ${erro ? styles.consentErro : ''}`}>
                <input
                    id={`${idBase}-consentimento`}
                    name="consentimento"
                    type="checkbox"
                    className={styles.checkbox}
                    checked={consentimento}
                    disabled={travado}
                    aria-invalid={erro ? true : undefined}
                    aria-describedby={erro ? `${idBase}-consentimento-erro` : undefined}
                    onChange={(e) => setConsentimento(e.target.checked)}
                />
                <span>{consentText}</span>
            </label>
            {erro ? (
                <span className={styles.erro} id={`${idBase}-consentimento-erro`} role="alert">
                    <Icone nome="alerta" tamanho={14} trecho={2} />
                    {erro}
                </span>
            ) : null}
        </div>
    );
};

/* ── Sucesso ───────────────────────────────────────────────────────────────── */

export const TelaSucesso = ({ nome, mensagem, reduzido }: { nome: string; mensagem: string; reduzido: boolean }) => {
    const ref = useRef<HTMLHeadingElement | null>(null);
    useEffect(() => {
        // leva o foco para o título: leitor de tela anuncia o sucesso sem ter de procurar
        const alvo = ref.current;
        if (alvo && typeof alvo.focus === 'function') alvo.focus({ preventScroll: true } as any);
    }, []);
    return (
        <div className={styles.sucesso} role="status" aria-live="polite">
            <motion.span
                className={styles.sucessoIcone}
                initial={reduzido ? false : { scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.05 }}
            >
                <Icone nome="check" tamanho={42} trecho={2.6} />
            </motion.span>
            <h3 className={styles.sucessoTitulo} tabIndex={-1} ref={ref}>
                {nome ? `Obrigado, ${nome}!` : 'Candidatura enviada!'}
            </h3>
            <p className={styles.sucessoTexto}>{mensagem}</p>
        </div>
    );
};
